import { Canvas as FabricCanvas, Group, TPointerEventInfo } from "fabric";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  BaseHighlightObject,
  BoxHighlightObject,
  ColumnHighlightObject,
  HighlightObjectModified,
  SpanHighlightObject,
} from "../../models/HighlightObject";
import { BackgroundImage } from "../canvas/BackgroundImage";
import { BorderedRect } from "../canvas/BorderedRect";
import { Canvas } from "../canvas/Canvas";
import { Column } from "../canvas/Column";
import { Rect } from "../canvas/Rect";
import { useHighlightState } from "../highlightProvider/HighlightProvider";
import { useSpanHighlightState } from "../highlightProvider/SpanHighlightProvider";
import { useImageWithSpans } from "../hooks/image/useImageWithSpans";

type Props = {
  // use a URL to initialize the base image
  imageUrl?: string;
  pageIndex: number;
  pageDimensions?: {
    width: number;
    height: number;
  };
};

const ImageViewer = (props: Props) => {
  const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const imageViewerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<FabricCanvas | undefined>(undefined);
  const highlightState = useHighlightState();
  const spanHighlightState = useSpanHighlightState();
  const boxesRef = useRef(highlightState.boxes);

  const [fabricCanvas, setFabricCanvas] = useState<FabricCanvas | undefined>();
  const [canvasWidth, setCanvasWidth] = useState<number>(1000);
  const [canvasHeight, setCanvasHeight] = useState<number>(1000);
  // TODO: move into hook
  const resizeCanvasFromImageViewerDiv = () => {
    clearTimeout(resizeTimeoutRef.current!);
    //console.debug('Scheduling resize');
    resizeTimeoutRef.current = setTimeout(() => {
      //console.debug('Resizing');
      if (imageViewerRef.current && canvasRef.current) {
        if (
          imageViewerRef.current.clientWidth != canvasRef.current.getWidth()
        ) {
          setCanvasHeight(imageViewerRef.current.clientHeight);
          setCanvasWidth(imageViewerRef.current.clientWidth);
          // fabricCanvas?.setWidth(imageViewerRef.current.clientHeight);
          // fabricCanvas?.setHeight(imageViewerRef.current.clientWidth);
          fabricCanvas?.renderAll();
        }
      }
    }, 150);
  };

  const handleResize = useCallback(resizeCanvasFromImageViewerDiv, []);

  // set canvas width based on imageViewerRef
  useEffect(() => {
    resizeCanvasFromImageViewerDiv();
  }, [imageViewerRef.current, canvasRef.current]);

  // resize canvas to width of imageViewerRef
  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return function removeResizeHandler() {
      window.removeEventListener("resize", handleResize);
    };
  }, [handleResize]);

  const moveOrResize = useCallback(
    (e: TPointerEventInfo) => {
      // console.info(e)
      const top = e.target?.top ?? 0;
      const left = e.target?.left ?? 0;

      const cursorY = e.absolutePointer?.y ?? 0;
      const cursorX = e.absolutePointer?.x ?? 0;

      const cursorYoffset = top + e.target!.height! - cursorY;
      const cursorXoffset = left + e.target!.width! - cursorX;

      let id: string | undefined = "";

      //
      let mouseMoveListener: (e: TPointerEventInfo) => void | undefined;
      let mouseUpFabricListener: (e: TPointerEventInfo) => void | undefined;

      if (
        (id = (e.subTargets ?? [])[0]?.data?.resizeId as string | undefined) !=
        undefined
      ) {
        // base the cursor tracking on the rect's own initial size, not the
        // group's - the group is always larger since it also encompasses the
        // oversized resize-handle triangle sticking out past the rect's
        // corner, so using the group's size here would leave the rect
        // permanently short of the actual cursor position
        const rectAtStart = (e.target as Group)._objects.find(
          (o) => o.data?.moveId,
        )!;
        const resizeCursorXoffset = left + rectAtStart.width! - cursorX;
        const resizeCursorYoffset = top + rectAtStart.height! - cursorY;

        mouseMoveListener = (e: TPointerEventInfo) => {
          e.target!.objectCaching = false;
          const rectObject = (e.target! as Group)._objects.find(
            (o) => o.data?.moveId,
          )!;
          const origW = rectObject.width!,
            origH = rectObject.height!;

          const newWidth = Math.max(
            (e.absolutePointer?.x ?? 0) + resizeCursorXoffset - left,
            (e.subTargets![0].data?.minWidth as number) ?? 0,
          );
          const newHeight = Math.max(
            (e.absolutePointer?.y ?? 0) + resizeCursorYoffset - top,
            (e.subTargets![0].data?.minHeight as number) ?? 0,
          );

          const widthInc = newWidth - origW;
          const heightInc = newHeight - origH;

          // grow the rect directly to the cursor-tracked size, from its
          // fixed top-left anchor (left/top untouched); keep the resize
          // handle triangle pinned to the rect's new bottom-right corner by
          // shifting it the same increment. The group's own width/height are
          // deliberately left untouched - updating them shifts the group's
          // own render origin (Fabric translates every object to its center
          // point internally, regardless of originX/Y), which would drag the
          // whole box out of sync with the cursor.
          (e.target! as Group)._objects.forEach((x) => {
            x.objectCaching = false;
            if (x.data?.moveId) {
              x.width = newWidth;
              x.height = newHeight;
            } else if (x.data?.resizeId) {
              x.left = x.left! + widthInc;
              x.top = x.top! + heightInc;
            }
          });
          // Fabric caches each object's corner coordinates (aCoords) and only
          // recomputes them on setCoords() - mutating width/height/left/top
          // directly never invalidates that cache, so getBoundingRect() would
          // otherwise keep returning the object's original, stale bounds
          (e.target! as Group).setCoords();

          // cursor less obtrusive
          canvasRef.current?.setCursor("default");
          canvasRef.current?.renderAll();
        };
        mouseUpFabricListener = (e: TPointerEventInfo) => {
          canvasRef.current?.off("mouse:move", mouseMoveListener);
          canvasRef.current?.off("mouse:up", mouseUpFabricListener);

          const rectAtMouseUp = (e.target! as Group)._objects.find(
            (o) => o.data?.moveId,
          )!;
          const origW = rectAtMouseUp.width!,
            origH = rectAtMouseUp.height!;

          const newWidth =
            (e.absolutePointer?.x ?? 0) + resizeCursorXoffset - left;
          const newHeight =
            (e.absolutePointer?.y ?? 0) + resizeCursorYoffset - top;

          // update size of group if not too small
          if (
            !(
              newWidth < ((e.subTargets![0]?.data?.minWidth as number) ?? 0) ||
              newHeight < ((e.subTargets![0]?.data?.minHeight as number) ?? 0)
            )
          ) {
            const widthInc = newWidth - origW;
            const heightInc = newHeight - origH;
            (e.target! as Group)._objects.forEach((x) => {
              if (x.data?.moveId) {
                x.width = newWidth;
                x.height = newHeight;
              } else if (x.data?.resizeId) {
                x.left = x.left! + widthInc;
                x.top = x.top! + heightInc;
              }
            });
            // refresh the cached corner coordinates now that geometry has
            // changed, otherwise getBoundingRect() below returns stale bounds
            (e.target! as Group).setCoords();
          }
          // convert widths and heights into percentages
          const target = e.target!;
          const rectObject = (target as Group)._objects ? (target as Group)._objects[0] : target;
          const bRect = rectObject.getBoundingRect();
          const newPercents: HighlightObjectModified = {
            leftXPercent: (bRect.left / canvasRef.current!.getWidth()) * 100,
            topYPercent: (bRect.top / canvasRef.current!.getHeight()) * 100,
            rightXPercent:
              ((bRect.width + bRect.left) / canvasRef.current!.getWidth()) *
              100,
            bottomYPercent:
              ((bRect.height + bRect.top) / canvasRef.current!.getHeight()) *
              100,
          };
          canvasRef.current?.setCursor("default");
          highlightObject?.objectMoved(newPercents);
        };
      } else if (
        (id = ((e.subTargets ?? [])[0]?.data?.moveId as string) || undefined) !=
        undefined
      ) {
        mouseMoveListener = (e: TPointerEventInfo) => {
          e.target!.left = (e.absolutePointer?.x ?? 0) + (left - cursorX);
          e.target!.top = (e.absolutePointer?.y ?? 0) + (top - cursorY);
          canvasRef.current?.renderAll();
        };
        mouseUpFabricListener = (e: TPointerEventInfo) => {
          canvasRef.current?.off("mouse:move", mouseMoveListener);
          canvasRef.current?.off("mouse:up", mouseUpFabricListener);

          e.target!.left = (e.absolutePointer?.x ?? 0) + (left - cursorX);
          e.target!.top = (e.absolutePointer?.y ?? 0) + (top - cursorY);

          const target = e.target!;
          const rectObject = (target as Group)._objects ? (target as Group)._objects[0] : target;
          const bRect = rectObject.getBoundingRect();
          // convert widths and heights into percentages
          const newPercents: HighlightObjectModified = {
            leftXPercent: (bRect.left / canvasRef.current!.getWidth()) * 100,
            topYPercent: (bRect.top / canvasRef.current!.getHeight()) * 100,
            rightXPercent:
              ((bRect.width + bRect.left) / canvasRef.current!.getWidth()) *
              100,
            bottomYPercent:
              ((bRect.height + bRect.top) / canvasRef.current!.getHeight()) *
              100,
          };
          // handle boxes going off screen
          if (
            newPercents.rightXPercent <= 0 ||
            newPercents.bottomYPercent <= 0 ||
            newPercents.leftXPercent >= 100 ||
            newPercents.topYPercent >= 100
          ) {
            newPercents.leftXPercent = 40;
            newPercents.rightXPercent = 60;
            newPercents.topYPercent = 40;
            newPercents.bottomYPercent = 60;
          }
          highlightObject?.objectMoved(newPercents);
        };
      } else {
        return;
      }
      const highlightObject: BaseHighlightObject | undefined =
        boxesRef.current.find((x) => x.id === id);
      if (!highlightObject) return;

      // setup mousemove event on canvas until mouseup on canvas. let fabric handle mouse up after leaving viewport
      canvasRef.current?.on("mouse:move", mouseMoveListener);
      canvasRef.current?.on("mouse:up", mouseUpFabricListener);
    },
    [canvasRef, boxesRef, canvasHeight, canvasWidth],
  );

  // set Ref value
  useEffect(() => {
    canvasRef.current = fabricCanvas;
    // setup event listener
    if (!canvasRef.current) return;
    canvasRef.current.on("mouse:down", moveOrResize);
  }, [fabricCanvas]);

  // TODO: convert to useAsync
  const [downloadedImage, loadingImage] = useImageWithSpans(
    props.imageUrl,
    spanHighlightState.enabled,
    spanHighlightState.boxes,
    props.pageDimensions,
  );

  // set HighlightProvider state in ref so we can access in objectModified event handler
  useEffect(() => {
    boxesRef.current = highlightState.boxes;
  }, [highlightState]);

  const columnBoxes = highlightState.boxes
    .filter((x) => x.type === "COLUMN" && x.visible)
    .map((x) => x as ColumnHighlightObject);

  const boxBoxes = highlightState.boxes
    .map((x) => x as BoxHighlightObject)
    .filter((x) => x.type === "BOX" && x.visible);

  const extractionBoxes = highlightState.boxes
    .map((x) => x as SpanHighlightObject)
    .filter((x) => x.type === "SPAN" && x.visible && x.bordered == false)
    .filter((x) => x.pageIndex === props.pageIndex);

  const reuseBoxes = highlightState.boxes
    .map((x) => x as SpanHighlightObject)
    .filter((x) => x.type === "SPAN" && x.visible && x.bordered == true)
    .filter((x) => x.pageIndex === props.pageIndex);

  return (
    <div
      className={`imageViewer ${loadingImage ? "downloading" : ""}`}
      ref={imageViewerRef}
    >
      <Canvas
        canvasId={"imageViewer"}
        setCanvas={setFabricCanvas}
        height={canvasHeight}
        width={canvasWidth}
      >
        <BackgroundImage
          key={downloadedImage?.getBackgroundKey()}
          canvasWidth={canvasWidth}
          objectUrl={downloadedImage?.objectUrl}
          getRealImageSizes={(data) => {
            setCanvasHeight(data.imgHeight);
          }}
        />

        {columnBoxes.length > 0 &&
          columnBoxes.map((box: ColumnHighlightObject, index) => {
            const left: number = (box.leftXPercent / 100) * canvasWidth;
            const right = (box.rightXPercent / 100) * canvasWidth;
            return (
              <Column
                key={box.id}
                id={box.id}
                color={box.color}
                opacity={box.opacity}
                draggable={false}
                width={right - left}
                x={left}
                evented={false}
              />
            );
          })}

        {boxBoxes.length > 0 &&
          boxBoxes.map((box, index) => {
            const left: number = (box.leftXPercent / 100) * canvasWidth;
            const right: number = (box.rightXPercent / 100) * canvasWidth;
            const top: number = (box.topYPercent / 100) * canvasHeight;
            const bottom: number = (box.bottomYPercent / 100) * canvasHeight;

            return (
              <Rect
                key={box.id}
                id={box.id}
                color={box.color}
                opacity={box.opacity}
                x={left}
                y={top}
                height={bottom - top}
                width={right - left}
                draggable={box.draggable}
                evented={true}
              />
            );
          })}

        {extractionBoxes.length > 0 &&
          props.pageDimensions &&
          extractionBoxes.map((box, index) => {
            const left: number =
              (box.x / props.pageDimensions!.width) * canvasWidth;
            const top: number =
              (box.y / props.pageDimensions!.height) * canvasHeight;
            const height: number =
              (box.height / props.pageDimensions!.height) * canvasHeight;
            const width: number =
              (box.width / props.pageDimensions!.width) * canvasWidth;

            return (
              <Rect
                key={box.id + index}
                id={box.id}
                color={box.color}
                opacity={box.opacity}
                x={left}
                y={top}
                height={height}
                width={width}
                draggable={false}
                evented={false}
              />
            );
          })}

        {reuseBoxes.length > 0 &&
          props.pageDimensions &&
          reuseBoxes.map((box, index) => {
            const left: number =
              (box.x / props.pageDimensions!.width) * canvasWidth;
            const top: number =
              (box.y / props.pageDimensions!.height) * canvasHeight;
            const height: number =
              (box.height / props.pageDimensions!.height) * canvasHeight;
            const width: number =
              (box.width / props.pageDimensions!.width) * canvasWidth;

            return (
              <BorderedRect
                key={box.id + index}
                id={box.id}
                color={box.color}
                opacity={box.opacity}
                x={left}
                y={top}
                height={height}
                width={width}
                draggable={false}
                evented={false}
              />
            );
          })}
        {/* {fabricCanvas?.renderAll() && null} */}
        {/* JOHN: THIS MIGHT BREAK THINGS */}
      </Canvas>
    </div>
  );
};

export { ImageViewer };
